import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UnauthorizedError } from '@/lib/api/errors';
import type { MutationToastMeta } from '@/lib/api/mutation-toast';

import {
  loginWithPassword,
  logout,
  resendForgotOtp,
  resendLoginOtp,
  resetPassword,
  sendForgotOtp,
  sendLoginOtp,
  verifyForgotOtp,
  verifyLoginOtp,
} from './api';
import { AUTH_SUCCESS_COPY, LOGOUT_COPY } from './constants';
import type { AuthSuccess } from './types';

/**
 * Auth keeps the app-wide **success** toast and owns only the **failure** half.
 *
 * `useLockoutNotice` already raises a sticky toast for a locked account, and `useAuthErrorToast`
 * has the auth-specific wording for the rest, so a second generic toast on top would double up on
 * every failed sign-in. Successes have no such conflict — a 2xx on any of these POSTs announces
 * itself in green from `<MutationToaster>`, per the app-wide rule.
 */
function authMeta(successMessage: string): MutationToastMeta {
  return { suppressErrorToast: true, successMessage };
}

export const authKeys = { all: ['auth'] as const, me: () => [...authKeys.all, 'me'] as const };

export function useLoginPassword() {
  const qc = useQueryClient();
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.SIGNED_IN),
    mutationFn: loginWithPassword,
    onSuccess: (d: AuthSuccess) => qc.setQueryData(authKeys.me(), d.user),
  });
}
export function useSendLoginOtp() {
  return useMutation({ meta: authMeta(AUTH_SUCCESS_COPY.CODE_SENT), mutationFn: sendLoginOtp });
}
export function useResendLoginOtp() {
  return useMutation({ meta: authMeta(AUTH_SUCCESS_COPY.CODE_RESENT), mutationFn: resendLoginOtp });
}
export function useVerifyLoginOtp() {
  const qc = useQueryClient();
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.SIGNED_IN),
    mutationFn: verifyLoginOtp,
    onSuccess: (d: AuthSuccess) => qc.setQueryData(authKeys.me(), d.user),
  });
}
export function useSendForgotOtp() {
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.CODE_SENT),
    mutationFn: (phone: string) => sendForgotOtp(phone),
  });
}
export function useResendForgotOtp() {
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.CODE_RESENT),
    mutationFn: (phone: string) => resendForgotOtp(phone),
  });
}
export function useVerifyForgotOtp() {
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.CODE_VERIFIED),
    mutationFn: verifyForgotOtp,
  });
}
export function useResetPassword() {
  return useMutation({
    meta: authMeta(AUTH_SUCCESS_COPY.PASSWORD_RESET),
    mutationFn: resetPassword,
  });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    // US-117535 (grilling Q6): the AC's own copy both ways — not the server's "…successfully."
    meta: { successMessage: LOGOUT_COPY.SUCCESS, errorMessage: LOGOUT_COPY.FAILED },
    // A 401 means the session is already gone server-side, which is what logging out wanted.
    mutationFn: () =>
      logout().catch((error: unknown) => {
        if (!(error instanceof UnauthorizedError)) throw error;
      }),
    onSuccess: () => qc.removeQueries({ queryKey: authKeys.all }),
  });
}
